import React, { useCallback, useEffect, useRef, useState } from 'react';

// components
import Button from '../../../components/Button/Button';
import alert from '../../../helpers/alert';
import { colors } from '../../../constants/colors';

// api
import getAppRefresh from '../../../api/reports/getAppRefresh';
import startAppRefresh from '../../../api/reports/startAppRefresh';
import importAppInstalls from '../../../api/reports/importAppInstalls';

import { errorText, importLines, isBusy, refreshSummary, sourceRows } from './appRefresh';

const POLL_MS = 4000;

const styles = {
  card: { marginTop: 20, marginBottom: 20, padding: '30px 20px', backgroundColor: 'white', border: '1px solid #e0e0e0' },
  title: { fontWeight: 700, fontSize: 20, lineHeight: '20px', color: colors.armadillo, margin: 0 },
  hint: { color: colors.dimGray, fontSize: 14, lineHeight: '20px', margin: '8px 0 0' },
  row: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 20, marginTop: 20 },
  section: { marginTop: 28, paddingTop: 20, borderTop: '1px solid #e0e0e0' },
  sources: { margin: '12px 0 0', padding: 0, listStyle: 'none', lineHeight: '26px' },
  link: { color: 'var(--brand-primary)', fontWeight: 700 },
};
const tones = { ok: colors.green, error: colors.red, busy: colors.orange, muted: colors.dimGray };

// ÐÐ±Ð½Ð¾Ð²Ð»ÐµÐ½Ð¸Ðµ ÑÐ°Ð±Ð»Ð¸ÑÑ Â«ÐÐ¾ÑÐ¾Ð½ÐºÐ° Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸ÑÂ» Ð² Google Ð¸ Ð·Ð°Ð³ÑÑÐ·ÐºÐ° Ð²ÑÐ³ÑÑÐ·Ð¾Ðº ÑÑÐ¾ÑÐ¾Ð².
// ÐÐ½Ð¾Ð¿ÐºÐ° Ð»Ð¸ÑÑ ÑÑÐ°Ð²Ð¸Ñ Ð·Ð°ÑÐ²ÐºÑ, Ð¾Ð±Ð½Ð¾Ð²Ð»ÐµÐ½Ð¸Ðµ Ð²ÑÐ¿Ð¾Ð»Ð½ÑÐµÑ ÑÐµÑÐ²ÐµÑ Ð² ÑÐµÑÐµÐ½Ð¸Ðµ Ð¼Ð¸Ð½ÑÑÑ; ÑÑÐ°ÑÑÑ Ð¾Ð¿ÑÐ°ÑÐ¸Ð²Ð°ÐµÐ¼, Ð¿Ð¾ÐºÐ° Ð¾Ð½Ð¾ Ð½Ðµ Ð·Ð°ÐºÐ¾Ð½ÑÐ¸ÑÑÑ.
const AppReportRefresh = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const fileInput = useRef(null);
  const wasBusy = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await getAppRefresh();
      if (res?.data) setStatus(res.data);
      setError('');
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const busy = isBusy(status);

  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [busy, load]);

  // ÐÐ±Ð½Ð¾Ð²Ð»ÐµÐ½Ð¸Ðµ Ð·Ð°ÐºÐ¾Ð½ÑÐ¸Ð»Ð¾ÑÑ (Ð±ÑÐ»Ð¾ Ð² ÑÐ°Ð±Ð¾ÑÐµ, Ð° ÑÐµÐ¿ÐµÑÑ Ð½ÐµÑ) â ÑÐ¾Ð¾Ð±ÑÐ°ÐµÐ¼ Ð¸ÑÐ¾Ð³
  useEffect(() => {
    if (wasBusy.current && !busy && status) {
      if (status.ok) alert.success('Ð¢Ð°Ð±Ð»Ð¸ÑÐ° Ð¾Ð±Ð½Ð¾Ð²Ð»ÐµÐ½Ð°');
      else alert.error(`ÐÐ±Ð½Ð¾Ð²Ð»ÐµÐ½Ð¸Ðµ Ð½Ðµ ÑÐ´Ð°Ð»Ð¾ÑÑ: ${status.message || 'Ð¿ÑÐ¸ÑÐ¸Ð½Ð° Ð½ÐµÐ¸Ð·Ð²ÐµÑÑÐ½Ð°'}`);
    }
    wasBusy.current = busy;
  }, [busy, status]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await startAppRefresh();
      if (res?.data) setStatus(res.data);
      setError('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    setUploading(true);
    setResults([]);
    try {
      const res = await importAppInstalls(formData);
      if (res?.data) {
        setStatus(res.data);
        setResults(importLines(res.data.files));
      }
      setError('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setUploading(false);
      // Ð¢Ð¾Ñ Ð¶Ðµ ÑÐ°Ð¹Ð» Ð¼Ð¾Ð¶Ð½Ð¾ Ð²ÑÐ±ÑÐ°ÑÑ Ð¿Ð¾Ð²ÑÐ¾ÑÐ½Ð¾
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const summary = refreshSummary(status);

  return (
    <div style={styles.card}>
      <p style={styles.title}>Ð¢Ð°Ð±Ð»Ð¸ÑÐ° Â«ÐÐ¾ÑÐ¾Ð½ÐºÐ° Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸ÑÂ» Ð² Google</p>
      <p style={styles.hint}>
        ÐÐ¾Ð»Ð½Ð¾ÑÑÑÑ Ð¾Ð±Ð½Ð¾Ð²Ð»ÑÐµÑÑÑ ÑÐ°Ð¼Ð° ÐºÐ°Ð¶Ð´ÑÐ¹ Ð´ÐµÐ½Ñ Ð² {status?.schedule || '09:30'} (ÐÐ»Ð¼Ð°ÑÑ): ÑÑÑÐ°Ð½Ð¾Ð²ÐºÐ¸, Ð°ÐºÑÐ¸Ð²Ð½Ð¾ÑÑÑ Ð²
        Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸Ð¸, Ð²Ð¾ÑÐ¾Ð½ÐºÐ° Ð¸ ÑÐ´ÐµÐ»ÐºÐ¸ Ð¸Ð· Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸Ñ. ÐÐ¸ÑÑ Â«ÐÐ¸Ð´ÑÂ» Ð¾Ð±Ð½Ð¾Ð²Ð»ÑÐµÑÑÑ ÐµÑÑ Ð¸ ÐºÐ°Ð¶Ð´ÑÐ¹ ÑÐ°Ñ.
      </p>

      <div style={styles.row}>
        <Button fill text="ÐÐ±Ð½Ð¾Ð²Ð¸ÑÑ ÑÐ°Ð±Ð»Ð¸ÑÑ" onClick={start} disabled={busy || starting || !status} />
        {status?.sheetUrl && (
          <a href={status.sheetUrl} target="_blank" rel="noreferrer" style={styles.link}>
            ÐÑÐºÑÑÑÑ ÑÐ°Ð±Ð»Ð¸ÑÑ
          </a>
        )}
        <span style={{ color: tones[summary.tone] }}>{summary.text}</span>
      </div>
      {error && <p style={{ ...styles.hint, color: colors.red }}>{error}</p>}

      <div style={styles.section}>
        <p style={{ ...styles.title, fontSize: 16 }}>ÐÐ°Ð½Ð½ÑÐµ ÑÑÐ¾ÑÐ¾Ð²</p>
        <p style={styles.hint}>
          Ð¡ÑÐ¾ÑÑ Ð½Ðµ Ð¾ÑÐ´Ð°ÑÑ ÑÑÑÐ°Ð½Ð¾Ð²ÐºÐ¸ Ð°Ð²ÑÐ¾Ð¼Ð°ÑÐ¸ÑÐµÑÐºÐ¸: Ð²ÑÐ³ÑÑÐ·Ð¸ÑÐµ CSV Ð¸Ð· App Store Connect (Â«ÐÐµÑÐ²ÑÐµ Ð·Ð°Ð³ÑÑÐ·ÐºÐ¸Â») Ð¸ Google Play
          (Â«ÐÑÑÐ¾ÑÐ½Ð¸ÐºÐ¸ ÑÑÐ°ÑÐ¸ÐºÐ°Â», Ð²ÑÐµ ÑÑÑÐ°Ð½Ñ) Ð¸ Ð·Ð°Ð³ÑÑÐ·Ð¸ÑÐµ ÑÑÐ´Ð°. ÐÐ»Ð°ÑÑÐ¾ÑÐ¼Ð° Ð¾Ð¿ÑÐµÐ´ÐµÐ»ÑÐµÑÑÑ Ð¿Ð¾ ÑÐ°Ð¹Ð»Ñ, Ð¿Ð¾ÑÐ»Ðµ Ð·Ð°Ð³ÑÑÐ·ÐºÐ¸ ÑÐ°Ð±Ð»Ð¸ÑÐ°
          Ð¾Ð±Ð½Ð¾Ð²Ð¸ÑÑÑ ÑÐ°Ð¼Ð°.
        </p>
        <ul style={styles.sources}>
          {sourceRows(status?.installs).map((row) => (
            <li key={row.title}>
              {row.title}: Ð´Ð°Ð½Ð½ÑÐµ Ð¿Ð¾ {row.lastDay}
              {row.stale && <span style={{ color: colors.orange }}> â Ð¿Ð¾ÑÐ° Ð·Ð°Ð³ÑÑÐ·Ð¸ÑÑ ÑÐ²ÐµÐ¶ÑÑ Ð²ÑÐ³ÑÑÐ·ÐºÑ</span>}
            </li>
          ))}
        </ul>
        <div style={styles.row}>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            multiple
            onChange={upload}
            disabled={uploading}
            data-testid="stores-csv"
          />
          {uploading && <span style={{ color: colors.dimGray }}>ÐÐ°Ð³ÑÑÐ¶Ð°ÐµÐ¼â¦</span>}
        </div>
        {results.length > 0 && (
          <ul style={styles.sources}>
            {results.map((r) => (
              <li key={r.text} style={{ color: r.ok ? colors.green : colors.red }}>
                {r.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AppReportRefresh;
